<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDashboardTopbar } from '@/composables/useDashboardTopbar'
import { useDashboardNotifications } from '@/composables/useDashboardNotifications'
import DashboardNavIcon from '@/components/dashboard/shell/DashboardNavIcon.vue'
import NotificationIndicator from '@/components/dashboard/ui/NotificationIndicator.vue'

defineProps<{
  mobileNavOpen: boolean
}>()

const emit = defineEmits<{
  toggleMobileNav: []
}>()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const {
  title,
  subtitle,
  showSubtitle,
  showAuthorisationStatusChip,
  isAuthorised,
  authorisationStatusLabel,
  authorisationChipAriaLabel,
} = useDashboardTopbar()
const { actionCount } = useDashboardNotifications()

const burgerLabel = (open: boolean) =>
  open ? t('Dashboard.Shell.Menu.Close') : t('Dashboard.Shell.Menu.Open')

const showMobileNotificationDot = computed(
  () => actionCount.value > 0 && route.meta.dashboardNav !== 'dashboard'
)

const onAuthorisationChipClick = () => {
  router.push({ name: 'Account' }).catch(() => {})
}
</script>

<template>
  <header class="sa-topbar">
    <div class="sa-topbar-left">
      <button
        type="button"
        class="sa-burger"
        data-tour-nav="menu"
        :aria-expanded="mobileNavOpen"
        aria-controls="dashboard-sidebar"
        :aria-label="
          showMobileNotificationDot
            ? t('Dashboard.Shell.Nav.DashboardWithNotifications', { count: actionCount })
            : burgerLabel(mobileNavOpen)
        "
        @click="emit('toggleMobileNav')"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
        <NotificationIndicator
          v-if="showMobileNotificationDot"
          variant="burger"
          :count="actionCount"
        />
      </button>
      <div class="sa-topbar-headings">
        <h1 class="sa-topbar-title">{{ title }}</h1>
        <p v-if="showSubtitle" class="sa-topbar-sub">{{ subtitle }}</p>
      </div>
    </div>
    <div v-if="showAuthorisationStatusChip" class="sa-topbar-right">
      <button
        type="button"
        class="sa-authorisation-chip"
        :class="isAuthorised ? 'sa-authorisation-chip--active' : 'sa-authorisation-chip--inactive'"
        :aria-label="authorisationChipAriaLabel"
        @click="onAuthorisationChipClick"
      >
        <span class="sa-authorisation-chip__icon" aria-hidden="true">
          <DashboardNavIcon :name="isAuthorised ? 'matching' : 'matching-inactive'" />
        </span>
        <span class="sa-authorisation-chip__text">
          {{ t('Dashboard.Shell.Topbar.AuthorisationChip.Title') }}
          <span
            class="sa-authorisation-chip__status"
            :class="isAuthorised ? 'sa-authorisation-chip__status--active' : 'sa-authorisation-chip__status--inactive'"
          >
            {{ authorisationStatusLabel }}
          </span>
        </span>
      </button>
    </div>
  </header>
</template>
